/**
 * Quick-create field contract + save payload for the Price Setup admin cards
 * (定價群組 / 數量定價排程).
 *
 * WHY THIS IS ITS OWN MODULE. The payload builder used to live inside
 * `GroupMatrixTab.tsx`, and its first guard re-declared a private copy of the
 * function rather than importing it — so the suite stayed green with the
 * defect fully reinstated. That is the `guard-must-reach-the-feature` failure:
 * a control that cannot fail. Importing the component instead is not an option
 * here, because vitest runs in `environment: 'node'` (vitest.config.ts:24) with
 * no DOM installed, and the component pulls antd in with it.
 *
 * So the rule the guard protects lives in a module with NO React and NO antd
 * imports: the component imports it, the node-env test imports the SAME
 * symbol, and breaking the function fails the suite.
 */

import { z } from 'zod';

export const quickCreateFieldsSchema = z.object({
  nameZh: z.string().trim().min(1, 'pricing.quickCreate.validation.nameZhRequired'),
  nameEn: z.string().trim().min(1, 'pricing.quickCreate.validation.nameEnRequired'),
  code: z.string().trim().min(1, 'pricing.quickCreate.validation.codeRequired'),
  /** Optional taxonomy link — present only on the groups card. */
  categoryId: z.string().nullable().optional(),
  /** 群組標準折扣 — groups card only; policy rate, not applied by the resolver. */
  defaultDiscountPct: z.number().min(0).max(100).nullable().optional(),
});

export type QuickCreateFields = z.infer<typeof quickCreateFieldsSchema>;

/**
 * The payload handed to the card's store write.
 *
 * SILENT-WRITE-LOSS FIX (2026-08-28): this used to be rebuilt from three named
 * fields, so `categoryId` and `defaultDiscountPct` — both collected by the
 * groups form — were stripped in transit. Both are optional on `PricingGroup`,
 * so nothing type-checked and the save still reported success: every group
 * created through the UI lost its taxonomy link and its standard rate.
 *
 * SPREAD the validated values so a field added to the form can never be
 * dropped by omission again; trim only on top. The guard asserts that
 * contract, not a field list — see `__tests__/pricingGroupCreatePayload`.
 */
export function buildQuickCreatePayload(values: QuickCreateFields): QuickCreateFields {
  return {
    ...values,
    code: values.code.trim(),
    nameEn: values.nameEn.trim(),
    nameZh: values.nameZh.trim(),
  };
}
